"use client";

import { useState, type ReactNode } from "react";

type Meal = {
  type: string;
  menu: string[];
  nutritionInfo: {
    calories: number;
    protein: string;
    fat: string;
    carbs: string;
    sodium: string;
  };
};

export type DayMenu = {
  day: string;
  meals: Meal[];
};

/**
 * 주간 식단표 — 섹션 헤딩(header prop) + 요일 세그먼티드 탭 + 아침/점심/저녁 카드.
 * 서버 컴포넌트(page.tsx)가 보존된 weeklyMenuVariations에서 한 주를 골라 prop으로 전달한다.
 * 데스크톱은 시안대로 헤딩과 탭을 같은 행에 배치(align-end, 탭 우측 정렬)하고,
 * 모바일은 헤딩 아래에 탭이 이어진다.
 */
export function WeeklyMenu({
  week,
  header,
}: {
  week: DayMenu[];
  header?: ReactNode;
}) {
  const [active, setActive] = useState(week[0]?.day ?? "");
  const current = week.find((d) => d.day === active) ?? week[0];

  return (
    <div>
      {/* 섹션 헤딩 + 요일 세그먼티드 탭 */}
      <div className="mb-4 flex flex-col gap-4 md:mb-7 md:flex-row md:flex-wrap md:items-end md:justify-between md:gap-6">
        {header}
        <div
          role="tablist"
          aria-label="요일 선택"
          className="inline-flex w-full gap-1 overflow-x-auto rounded-xl bg-fill p-1 md:w-auto"
        >
          {week.map((d) => {
            const selected = d.day === active;
            return (
              <button
                key={d.day}
                role="tab"
                aria-selected={selected}
                onClick={() => setActive(d.day)}
                className={`flex-1 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors md:flex-none md:px-4 ${
                  selected
                    ? "bg-surface text-brand shadow-[0_1px_4px_rgba(112,115,124,0.1)]"
                    : "text-label-alt hover:text-label"
                }`}
              >
                {d.day.replace("요일", "")}
              </button>
            );
          })}
        </div>
      </div>

      {/* 아침·점심·저녁 카드 */}
      <div className="grid gap-3 md:grid-cols-3 md:gap-4">
        {current.meals.map((meal) => (
          <div
            key={meal.type}
            className="flex flex-col overflow-hidden rounded-2xl border border-line"
          >
            <div className="flex items-center justify-between border-b border-line-weak px-5 py-3.5">
              <span className="text-[17px] font-bold text-brand">
                {meal.type}
              </span>
            </div>
            <div className="flex flex-1 flex-col gap-1.5 px-5 py-4 pb-5">
              {meal.menu.map((item) => (
                <span key={item} className="text-[15px] text-label">
                  {item}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-3 text-xs text-label-assistive">
        * 예시 식단이에요. 실제 식단은 요양원별 맞춤 설계에 따라 달라져요.
      </p>
    </div>
  );
}
